import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import StorefrontLayout from "../../components/storefront/StorefrontLayout";
import { api } from "../../lib/api";
import { fmtINR, waLink } from "../../lib/constants";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Heart, ShoppingBag, ShieldCheck, Award, MessageCircle, Sparkles, Palette } from "lucide-react";
import HallmarkViewer from "../../components/HallmarkViewer";
import CustomiseDesign from "../../components/CustomiseDesign";
import ZoomImage from "../../components/ZoomImage";
import AnimatedActionIcon from "../../components/AnimatedActionIcon";
import cartAnim from "../../assets/add-to-cart.json";
import likeAnim from "../../assets/like.json";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [price, setPrice] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [busy, setBusy] = useState(false);
  // Customisation panel toggle (the flow lives in <CustomiseDesign/>)
  const [showCustom, setShowCustom] = useState(false);
  const [cartTrigger, setCartTrigger] = useState(0);
  const [wishTrigger, setWishTrigger] = useState(0);
  const [stock, setStock] = useState(null);   // live inventory for this design
  const [breakdown, setBreakdown] = useState(null);  // full price breakup incl. HUID charges
  // The weights/sizes this design is stocked in, and which one the customer picked.
  const [variants, setVariants] = useState([]);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    setVariants([]); setVariant(null); setBreakdown(null);
    api.get(`/api/products/${id}`).then((r) => {
      setProduct(r.data);
      const cat = r.data?.inventory_category || r.data?.category;
      if (cat) {
        api.get(`/api/inventory/stock/${encodeURIComponent(cat)}`).then((s) => setStock(s.data)).catch(() => {});
        // Designs stocked in several weights offer them as choices here.
        api.get(`/api/inventory/variants/${encodeURIComponent(cat)}`).then((v) => {
          const list = v.data?.variants || [];
          setVariants(list);
          // Pre-select the first choice that is actually in stock.
          setVariant(list.find((x) => x.in_stock) || list[0] || null);
        }).catch(() => {});
      }
    }).catch(() => nav("/catalog"));
  }, [id, nav]);

  // Price follows the chosen weight/size — each piece has its own weight and
  // its own hallmarking charge, so the total moves when the choice changes.
  useEffect(() => {
    if (!id) return;
    const qs = variant?.pick ? `?variant_label_no=${encodeURIComponent(variant.pick)}` : "";
    api.get(`/api/products/${id}/price${qs}`).then((r) => {
      setPrice(r.data.price);
      setBreakdown(r.data.breakdown || null);
    }).catch(() => {});
  }, [id, variant]);

  if (!product) return <StorefrontLayout><div className="px-12 py-24 text-center text-ash">Loading…</div></StorefrontLayout>;

  // What the page shows for weight/purity: the chosen piece when the design is
  // stocked in several weights, otherwise the product's own figures.
  const displayWeight = variant?.weight_grams ?? product.weight_grams;
  const displayPurity = variant?.purity ?? product.purity;
  // A per-piece product IS one specific piece — its title already names the
  // weight, so offering other weights on it would contradict itself. Those
  // pieces are browsed as separate products instead.
  const hasChoices = !product.per_piece && variants.length > 1;
  const soldOut = hasChoices
    ? !variants.some((v) => v.in_stock)
    : stock?.state === "sold_out";

  const addToCart = async () => {
    if (!user) { toast.error("Please login first"); nav("/login"); return; }
    if (hasChoices && !variant) { toast.error("Please choose a weight first"); return; }
    if (hasChoices && !variant.in_stock) { toast.error(`${variant.label} is out of stock`); return; }
    if (!hasChoices && soldOut) { toast.error("This piece is sold out"); return; }
    setBusy(true);
    try {
      await api.post("/api/cart/add", {
        product_id: product.id, qty: 1,
        // Which exact piece the customer picked, so billing hands over that one.
        variant_label_no: variant?.pick || "",
      });
      setCartTrigger((t) => t + 1);
      toast.success(hasChoices ? `Added to cart · ${variant.label}` : "Added to cart");
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const toggleWishlist = async () => {
    if (!user) { toast.error("Please login first"); nav("/login"); return; }
    try {
      const { data } = await api.post("/api/wishlist/toggle", { product_id: product.id, qty: 1 });
      if (data.action === "added") setWishTrigger((t) => t + 1);
      toast.success(`Wishlist ${data.action}`);
    } catch { toast.error("Failed"); }
  };

  // Price breakup — deliberately kept to what the shop is happy to show a
  // customer: metal, weight, that making + wastage applies (never the figure),
  // stone charges when there are any, hallmarking, and the total. The
  // calculation behind it is untouched; this only controls what is displayed.
  const breakdownRows = () => {
    if (!product) return [];
    const b = breakdown;

    // Gemstone: carat, rate and certification — never weight or hallmarking.
    if (product.metal === "Gemstone") {
      const carat = b?.carat ?? product.carat_weight ?? 0;
      const rate = b?.rate_per_carat ?? product.rate_per_carat ?? 0;
      const cert = b?.certification ?? product.certification_charge ?? 3000;
      return [
        ["Type", `${product.gem_type || product.category}`],
        ["Carat", `${carat} ct`],
        ["Rate / carat", fmtINR(rate)],
        ["Certification charges", fmtINR(cert)],
        ["Total", fmtINR(b?.total ?? price ?? 0)],
      ];
    }

    const metalName = b?.metal || product.metal;
    // Silver & gemstone carry no karat purity — show the metal name only.
    const noKarat = ["silver", "gemstone"].includes(String(metalName || "").toLowerCase());
    const rows = [
      ["Metal", noKarat ? metalName : [metalName, b?.purity || displayPurity].filter(Boolean).join(" ")],
      ["Weight", `${b?.weight_grams ?? displayWeight} g`],
    ];

    // Say that making + wastage is part of the price, but not the percentage
    // or the rupee value.
    if (b ? b.mode === "calculated" : product.has_making_wastage !== false) {
      rows.push(["Making + Wastage", "Applicable"]);
    }
    // Only when the piece actually carries stone charges.
    if ((b?.stone_charges ?? product.stone_charges ?? 0) > 0) {
      rows.push(["Stone charges", fmtINR(b?.stone_charges ?? product.stone_charges)]);
    }
    // Hallmarking is charged on gold only.
    if (b?.huid_applicable) {
      rows.push(["HUID charges", b.huid_included_in_price ? "Included" : fmtINR(b.huid_charges)]);
    }
    rows.push(["Total", fmtINR(b?.total ?? price ?? 0)]);
    if (product.huid) rows.push(["HUID no.", product.huid]);
    return rows;
  };

  const isGem = product.metal === "Gemstone";

  return (
    <StorefrontLayout>
      <div className="px-6 lg:px-12 py-10 grid lg:grid-cols-2 gap-12" data-testid="product-detail">
        {/* Gallery */}
        <div>
          <div className="aspect-square bg-white border border-line overflow-hidden">
            <ZoomImage src={product.images?.[activeImg] || product.images?.[0]} alt={product.name} className="w-full h-full" />
          </div>
          {product.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`aspect-square border ${activeImg === i ? "border-maroon" : "border-line"}`} data-testid={`product-thumb-${i}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <div className="eyebrow">{product.category} · {product.gender}</div>
          <h1 className="font-display text-4xl lg:text-5xl text-maroon mt-2">{product.name}</h1>
          {!isGem && (
            <div className="mt-3 flex items-center gap-3 text-xs text-ash">
              <span className="flex items-center gap-1"><ShieldCheck size={14} className="text-gold"/> BIS Hallmark</span>
              {product.huid && <span className="flex items-center gap-1"><Award size={14} className="text-gold"/> HUID {product.huid}</span>}
            </div>
          )}
          <div className="royal-divider my-6" />

          <div className="text-3xl font-semibold tracking-tight text-maroon" data-testid="product-price">{price ? fmtINR(price) : "—"}</div>
          <div className="text-[11px] text-ash mt-1">Live-calculated · Updates with daily metal rate</div>

          {/* Weight / size picker — one button per stocked weight, each showing
              its own price and how many of THAT weight are left. */}
          {hasChoices && (
            <div className="mt-5" data-testid="variant-picker">
              <div className="eyebrow mb-2">Available weights</div>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => {
                  const on = variant?.key === v.key;
                  return (
                    <button
                      key={v.key}
                      onClick={() => v.in_stock && setVariant(v)}
                      disabled={!v.in_stock}
                      title={v.in_stock ? v.stock_message : "Out of stock"}
                      className={`px-3 py-2 border text-left min-w-[104px] ${
                        on ? "border-maroon bg-maroon/5"
                        : v.in_stock ? "border-line hover:border-gold"
                        : "border-line opacity-40 cursor-not-allowed line-through"}`}
                      data-testid={`variant-${v.key}`}
                    >
                      <div className="text-sm font-medium">{v.label}</div>
                      <div className="text-[11px] text-maroon">{fmtINR(v.price)}</div>
                      <div className={`text-[10px] ${v.available <= 3 && v.available > 0 ? "text-amber-700" : "text-ash"}`}>
                        {v.stock_message}
                      </div>
                    </button>
                  );
                })}
              </div>
              {variant && (
                <div className="text-[11px] text-ash mt-2" data-testid="variant-selected">
                  Selected: {variant.label} · {variant.stock_message}
                </div>
              )}
            </div>
          )}

          {!hasChoices && stock && stock.state !== "made_to_order" && (
            <div className="mt-3" data-testid="product-stock">
              {stock.state === "sold_out" ? (
                <span className="inline-block text-xs uppercase tracking-widest bg-red-100 text-red-700 px-3 py-1 rounded-full">Sold Out</span>
              ) : stock.state === "low" ? (
                <span className="inline-block text-xs bg-amber-100 text-amber-800 px-3 py-1 rounded-full font-medium">{stock.message}</span>
              ) : (
                <span className="inline-block text-xs bg-green-100 text-green-700 px-3 py-1 rounded-full">In stock</span>
              )}
            </div>
          )}

          <p className="mt-6 text-sm text-ink leading-relaxed">{product.description}</p>

          {/* Price breakdown */}
          <div className="mt-6 bg-white border border-line p-5">
            <div className="eyebrow mb-3">Price breakup</div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {breakdownRows().map(([k, v]) => (
                <React.Fragment key={k}><div className="text-ash">{k}</div><div className="text-right">{v}</div></React.Fragment>
              ))}
            </div>
            <div className="mt-3 text-[10px] text-ash">
              {isGem
                ? "Certification charges and GST are included in the price above."
                : breakdown?.huid_applicable
                  ? "Hallmarking (HUID) charges and 3% GST are included in the total above · Lock today's rate by paying a token in store."
                  : "GST is included in the total above · Lock today's rate by paying a token in store."}
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <button onClick={() => addToCart()} disabled={busy || soldOut} className="btn-maroon col-span-2 sm:col-span-1 justify-center disabled:opacity-50" data-testid="add-to-cart-btn">
              <AnimatedActionIcon animationData={cartAnim} trigger={cartTrigger} size={18} icon={<ShoppingBag size={14}/>} /> Add to Cart
            </button>
            <button onClick={toggleWishlist} className="btn-outline justify-center" data-testid="add-to-wishlist-btn">
              <AnimatedActionIcon animationData={likeAnim} trigger={wishTrigger} size={18} icon={<Heart size={14}/>} /> Wishlist
            </button>
            <a href={waLink(`Hi, I'd like to know more about ${product.name} (ID ${product.id})`)} target="_blank" rel="noreferrer" className="btn-outline justify-center" data-testid="enquire-whatsapp">
              <MessageCircle size={14}/> Enquire
            </a>
          </div>

          {/* Customisation — not available for gemstones */}
          {!isGem && (
          <div className="mt-4">
            {!showCustom ? (
              <button
                onClick={() => setShowCustom(true)}
                className="btn-outline w-full justify-center"
                data-testid="customise-toggle"
              >
                <Palette size={14}/> Customise this design
              </button>
            ) : (
              <div className="bg-white border-2 border-gold p-5" data-testid="customise-panel">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Palette size={16} className="text-maroon"/>
                    <h3 className="font-display text-xl text-maroon">Customise a design</h3>
                  </div>
                  <button onClick={() => setShowCustom(false)} className="text-xs text-ash hover:text-maroon">Cancel</button>
                </div>
                <CustomiseDesign baseProduct={product} onAdded={() => setShowCustom(false)} />
              </div>
            )}
          </div>
          )}
          {/* Reassurance — gold/silver only (gemstones aren't hallmarked) */}
          {!isGem && (
          <div className="mt-8 grid grid-cols-3 gap-3 text-center text-[11px] text-ash">
            <div className="bg-white border border-line p-3"><Sparkles size={16} className="mx-auto text-gold mb-1"/>Lifetime exchange</div>
            <div className="bg-white border border-line p-3"><ShieldCheck size={16} className="mx-auto text-gold mb-1"/>BIS Certified</div>
            <div className="bg-white border border-line p-3"><Award size={16} className="mx-auto text-gold mb-1"/>HUID Traceable</div>
          </div>
          )}

          <Link to="/astrology" className="block mt-6 bg-maroon text-pearl p-4 text-center text-sm" data-testid="cta-astrologer-on-pdp">
            Unsure if it suits you? <span className="text-gold-light underline">Ask our astrologer →</span>
          </Link>

          {!isGem && <LockRateInline productId={product.id} purity={displayPurity} weight={displayWeight} />}

          {!isGem && product.huid && (
            <div className="mt-6"><HallmarkViewer huid={product.huid} /></div>
          )}
        </div>
      </div>
    </StorefrontLayout>
  );
}

function LockRateInline({ productId, purity, weight }) {
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ customer_name: "", customer_phone: "" });
  const [tokenAmount, setTokenAmount] = useState(500);
  const [phoneError, setPhoneError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    // Token amount is configured by the admins in Site Settings
    api.get("/api/site-settings").then((r) => {
      if (r.data?.lock_rate_token_amount) setTokenAmount(r.data.lock_rate_token_amount);
    }).catch(() => {});
  }, []);

  // Exactly 10 digits, Indian mobile (starts 6-9)
  const validatePhone = (digits) => /^[6-9]\d{9}$/.test(digits);

  const verify = async (bookingId, orderId, paymentId, signature) => {
    const { data } = await api.post("/api/lock-rate/verify", {
      booking_id: bookingId,
      razorpay_order_id: orderId,
      razorpay_payment_id: paymentId,
      razorpay_signature: signature,
    });
    setDone(data);
    toast.success("Token paid — rate locked!");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validatePhone(form.customer_phone)) {
      setPhoneError("Enter a valid 10-digit mobile number starting with 6-9.");
      return;
    }
    setPhoneError("");
    setBusy(true);
    try {
      // Step 1 — create booking + payment order for the token amount
      const { data } = await api.post("/api/lock-rate/create-order", {
        ...form, product_id: productId, desired_purity: purity, desired_weight: weight,
      });
      const booking = data.booking;
      if (data.razorpay.mock) {
        // Test mode — auto-verify
        await verify(booking.id, data.razorpay.order_id, `pay_mock_${Date.now()}`, "mock-sig");
      } else {
        // Live Razorpay checkout
        const rzp = new window.Razorpay({
          key: data.razorpay.key_id,
          amount: data.razorpay.amount,
          currency: data.razorpay.currency,
          order_id: data.razorpay.order_id,
          name: "Swayambhu Gold",
          description: `Rate-lock token · ${purity} @ locked rate`,
          prefill: { name: form.customer_name, contact: form.customer_phone },
          handler: async (resp) => {
            try {
              await verify(booking.id, resp.razorpay_order_id, resp.razorpay_payment_id, resp.razorpay_signature);
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Payment verification failed");
            }
          },
        });
        rzp.open();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Login required to lock the rate");
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="mt-4 bg-gold/10 border border-gold p-4 text-sm space-y-1" data-testid="lock-rate-done">
      <div className="font-medium text-maroon">✓ Rate locked at ₹{done.locked_rate_per_g}/g for {purity}</div>
      <div className="text-xs text-ash">Valid until {done.valid_until?.slice(0, 10)}</div>
      <div className="text-xs text-ash">Transaction ID: {done.transaction_id}</div>
      <div className="text-xs text-ash">Paid: {fmtINR(done.token_amount)} · {done.paid_at ? new Date(done.paid_at).toLocaleString() : ""}</div>
      <div className="text-[10px] text-ash mt-1">You can view this booking any time under My Orders → My Rate Locks.</div>
    </div>
  );

  return (
    <div className="mt-6 bg-white border border-line">
      <button onClick={() => setShow(!show)} className="w-full p-3 text-left text-sm font-medium flex items-center justify-between" data-testid="lock-rate-toggle">
        🔒 Lock Today's Rate (token booking) <span className="text-xs text-ash">{show ? "−" : "+"}</span>
      </button>
      {show && (
        <form onSubmit={submit} className="p-4 border-t border-line space-y-3">
          <p className="text-xs text-ash">Pay a token of {fmtINR(tokenAmount)} to lock today's gold rate for 7 days.</p>
          <input required placeholder="Your name" className="w-full border border-line px-3 py-2 bg-pearl" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} data-testid="lock-name"/>
          <input
            required
            type="tel"
            inputMode="numeric"
            maxLength={10}
            placeholder="10-digit mobile number"
            className={`w-full border px-3 py-2 bg-pearl ${phoneError ? "border-red-500" : "border-line"}`}
            value={form.customer_phone}
            onChange={(e) => {
              // Digits only, hard cap at 10
              const v = e.target.value.replace(/\D/g, "").slice(0, 10);
              setForm({ ...form, customer_phone: v });
              if (phoneError && validatePhone(v)) setPhoneError("");
            }}
            data-testid="lock-phone"
          />
          {phoneError && <p className="text-xs text-red-600" data-testid="lock-phone-error">{phoneError}</p>}
          <button disabled={busy} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="lock-submit">
            {busy ? "Processing…" : `Lock Rate · Pay ${fmtINR(tokenAmount)} Token`}
          </button>
        </form>
      )}
    </div>
  );
}